/**
 * OnboardingWizard — Modale post-login en 3 étapes éclair pour les nouveaux users.
 *
 * Apparaît si :
 *   - User connecté
 *   - `user.onboarding_completed === false`
 *
 * Persistance : PUT /api/auth/onboarding à chaque étape (autosave) + flag final.
 * Skip toléré : "Plus tard" → flip `onboarding_completed: true` quand même.
 *
 * Inspiré de l'onboarding Linear/Notion : 1 question max par écran, choix
 * en grandes tuiles cliquables, CTA primary plein de gradient gold, progress bar.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Home, Briefcase, TrendingUp, Hammer, MoreHorizontal,
  MapPin, FolderPlus, Upload, Sparkles, ChevronRight, ChevronLeft,
  Check, X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: 'acheteur',     label: 'Acheteur / Investisseur particulier', icon: Home,            desc: 'Je cherche un bien à acquérir' },
  { id: 'architecte',   label: 'Architecte / Maître d\'œuvre',         icon: Briefcase,       desc: 'Je conçois et pilote des programmes' },
  { id: 'investisseur', label: 'Investisseur / Foncier',               icon: TrendingUp,      desc: 'J\'investis dans des opérations immobilières' },
  { id: 'maitre_oeuvre',label: 'Promoteur / Maître d\'ouvrage',        icon: Hammer,          desc: 'Je porte des opérations promotrices' },
  { id: 'autre',        label: 'Autre',                                 icon: MoreHorizontal,  desc: 'Je préciserai dans mon profil' },
];

const MARKETS = [
  { id: 'marrakech',  label: 'Marrakech',  flag: '🇲🇦' },
  { id: 'casablanca', label: 'Casablanca', flag: '🇲🇦' },
  { id: 'rabat',      label: 'Rabat',      flag: '🇲🇦' },
  { id: 'tanger',     label: 'Tanger',     flag: '🇲🇦' },
  { id: 'france',     label: 'France',     flag: '🇫🇷' },
  { id: 'autre',      label: 'Autre / multi', flag: '🌍' },
];

const FIRST_ACTIONS = [
  { id: 'create_project',  label: 'Créer mon premier projet',       icon: FolderPlus, route: '/projects?new=1' },
  { id: 'import_contacts', label: 'Importer mes contacts (CSV/vCard)', icon: Upload,    route: '/crm?import=1' },
  { id: 'explore_demo',    label: 'Découvrir la démo Menara',       icon: Sparkles,   route: '/projects/proj_b35bdffea759' },
  { id: 'skip',            label: 'Explorer librement',             icon: ChevronRight, route: '/studio/dashboard' },
];

const Tile = ({ active, onClick, icon: Icon, label, desc, prefix, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-left rounded-xl p-4 transition-all"
    style={{
      background: active ? 'var(--c-gold-soft)' : 'var(--c-surface-2)',
      border: `1px solid ${active ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
      boxShadow: active ? '0 0 18px rgba(217,160,91,0.25)' : 'none',
    }}
  >
    <div className="flex items-start gap-3">
      {prefix && <span className="text-xl shrink-0">{prefix}</span>}
      {Icon && (
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: active ? 'rgba(217,160,91,0.18)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${active ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
          }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: active ? 'var(--c-gold)' : 'var(--c-text-2)' }} />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold" style={{ color: 'var(--c-text)', fontFamily: 'Outfit, sans-serif' }}>
          {label}
        </p>
        {desc && <p className="text-[11.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>{desc}</p>}
      </div>
      {active && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--c-gold)' }} />}
    </div>
  </button>
);

const OnboardingWizard = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({
    profile_type: null,
    target_market: null,
    first_action: null,
  });

  // Show when user is signed-in but profile-setup wizard incomplete.
  // We use a flag distinct from `onboarding_completed` (which is the 5-step
  // legacy product tour) so the two onboardings can run in sequence:
  // (1) personalization wizard → (2) product tour.
  useEffect(() => {
    if (!user) return setOpen(false);
    if (user.profile_setup_completed === true) return setOpen(false);
    setOpen(true);
  }, [user]);

  const persist = useCallback(async (extra = {}, completed = false) => {
    const payload = { ...answers, ...extra };
    if (completed) payload.profile_setup_completed = true;
    try {
      await axios.put(`${API}/auth/onboarding`, payload, { withCredentials: true });
    } catch (e) {
      // Non-bloquant : on continue le wizard même si la persistance échoue
      // eslint-disable-next-line no-console
      console.warn('[onboarding] save failed', e);
    }
  }, [answers]);

  const next = async () => {
    if (step < 2) {
      await persist();
      setStep((s) => s + 1);
    } else {
      // Final step → mark complete + route to chosen first action
      setBusy(true);
      const target = FIRST_ACTIONS.find((f) => f.id === answers.first_action);
      await persist({}, true);
      await checkAuth();
      setBusy(false);
      setOpen(false);
      toast.success('Bienvenue chez Peter\'s Universe ✨');
      if (target?.route) navigate(target.route);
    }
  };

  const skip = async () => {
    setBusy(true);
    await persist({}, true);
    await checkAuth();
    setBusy(false);
    setOpen(false);
  };

  const canNext =
    (step === 0 && !!answers.profile_type) ||
    (step === 1 && !!answers.target_market) ||
    (step === 2 && !!answers.first_action);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="onboarding-wizard"
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      style={{ background: 'rgba(6,8,12,0.86)', backdropFilter: 'blur(14px)' }}
    >
      <div
        className="relative w-full max-w-xl rounded-2xl overflow-hidden"
        style={{
          background: 'var(--c-surface-1)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)',
        }}
      >
        {/* Skip */}
        <button
          type="button"
          onClick={skip}
          disabled={busy}
          data-testid="onboarding-skip"
          aria-label="Passer le wizard"
          className="absolute top-3 right-3 rounded-lg p-1.5 transition-colors z-10"
          style={{ color: 'var(--c-text-faint)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-faint)'; }}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress bar */}
        <div className="h-1 w-full" style={{ background: 'var(--c-surface-2)' }}>
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${((step + 1) / 3) * 100}%`,
              background: 'linear-gradient(90deg, var(--c-gold) 0%, var(--c-gold-soft) 100%)',
              boxShadow: '0 0 12px rgba(217,160,91,0.45)',
            }}
            data-testid="onboarding-progress"
          />
        </div>

        <div className="px-6 sm:px-8 py-7">
          <span className="hud-eyebrow hud-eyebrow-gold">
            Étape {step + 1} / 3
          </span>
          <h2
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 22 }}
          >
            {step === 0 && `Bienvenue ${user?.first_name || user?.name?.split(' ')[0] || ''} 👋`}
            {step === 1 && 'Quelle est ta zone de chasse ?'}
            {step === 2 && 'Par où veux-tu commencer ?'}
          </h2>
          <p className="mt-1.5 text-[13.5px]" style={{ color: 'var(--c-text-2)' }}>
            {step === 0 && 'Quelle est ta posture sur l\'immobilier ?'}
            {step === 1 && 'On utilisera cette info pour pré-cibler les rendus 3D et les contacts CRM.'}
            {step === 2 && '60 secondes pour transformer Peter\'s Universe en ton cockpit personnel.'}
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {step === 0 && ROLES.map((r) => (
              <Tile
                key={r.id}
                active={answers.profile_type === r.id}
                onClick={() => setAnswers((a) => ({ ...a, profile_type: r.id }))}
                icon={r.icon}
                label={r.label}
                desc={r.desc}
                testid={`onboarding-role-${r.id}`}
              />
            ))}
            {step === 1 && MARKETS.map((m) => (
              <Tile
                key={m.id}
                active={answers.target_market === m.id}
                onClick={() => setAnswers((a) => ({ ...a, target_market: m.id }))}
                prefix={m.flag}
                label={m.label}
                icon={MapPin}
                testid={`onboarding-market-${m.id}`}
              />
            ))}
            {step === 2 && FIRST_ACTIONS.map((f) => (
              <Tile
                key={f.id}
                active={answers.first_action === f.id}
                onClick={() => setAnswers((a) => ({ ...a, first_action: f.id }))}
                icon={f.icon}
                label={f.label}
                testid={`onboarding-action-${f.id}`}
              />
            ))}
          </div>

          {/* Footer actions */}
          <div className="mt-6 flex items-center justify-between gap-3 pt-4" style={{ borderTop: '1px solid var(--c-border)' }}>
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || busy}
              data-testid="onboarding-back"
              className="inline-flex items-center gap-1.5 px-3 h-9 text-[12px] font-semibold rounded-lg transition-colors disabled:opacity-30"
              style={{ color: 'var(--c-text-2)' }}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Retour
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={skip}
                disabled={busy}
                data-testid="onboarding-skip-text"
                className="text-[11.5px] underline-offset-2 hover:underline transition-colors"
                style={{ color: 'var(--c-text-faint)' }}
              >
                Plus tard
              </button>
              <button
                type="button"
                onClick={next}
                disabled={!canNext || busy}
                data-testid="onboarding-next"
                className="inline-flex items-center gap-1.5 px-4 h-9 text-[12px] font-semibold rounded-lg transition-all disabled:opacity-40"
                style={{
                  background: 'var(--c-gold)',
                  color: '#0E1116',
                  boxShadow: '0 4px 14px rgba(217,160,91,0.32)',
                }}
              >
                {step < 2 ? 'Suivant' : (busy ? 'Finalisation…' : 'Démarrer')}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
